import {
  IonAvatar,
  IonButton,
  IonContent,
  IonFooter,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonSpinner,
  useIonToast,
} from '@ionic/react';
import { printOutline, bluetoothOutline } from 'ionicons/icons';
import React, { useCallback } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { t } from 'ttag';

import { PageTitle } from '@components/PageTitle';
import { SectionListHeader } from '@components/SectionTitle';
import TabLayout from '@components/TabLayout';
import useLoading from '@hooks/useLoading';
import { usePrinter } from '@hooks/usePrinter';
import { useEventAndConfiguration } from '@pages/Event/queries';
import { useFindBluetoothPrinters, BadgePrinter } from '~capacitor-plugins/BadgePrinter';

const Icon = styled(IonIcon)`
  font-size: 36px;
`;

const SelectedPrinter = styled.div`
  font-weight: 500;
`;

const Footer = styled(IonFooter)`
  padding: 0 32px 32px;
`;

const SearchAgainText = styled.p`
  font-size: 1.25rem; /* 20px */
  text-align: center;
`;

const Button = styled(IonButton)`
  margin-top: 16px;
`;

const Selection = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const [{ data: event, isFetching: isEventFetching }] = useEventAndConfiguration(eventId);
  const [presentToast, dismissToast] = useIonToast();
  const { isLoadingSettings, saveSettings, settings, selectedPrinter } = usePrinter();
  const {
    isFetching: isFetchingPrinters,
    data: discoveredPrinters,
    refetch,
  } = useFindBluetoothPrinters();

  useLoading(isEventFetching || isLoadingSettings);

  const rescan = useCallback(async () => {
    const result = await refetch();
    await dismissToast();
    presentToast({
      message: !result.data?.length
        ? t`Scanning complete. No printers were found.`
        : t`Scanning complete.`,
      duration: 5000,
    });
  }, [dismissToast, presentToast, refetch]);

  if (!event || !settings) {
    return null;
  }

  // Filter out the currently selected printer, if there is one
  const selectablePrinters = settings.printer?.serialNumber
    ? discoveredPrinters?.filter(printer => printer.serialNumber !== settings.printer?.serialNumber)
    : discoveredPrinters;

  return (
    <TabLayout title={event.name}>
      <IonContent>
        <PageTitle>{t`Printer`}</PageTitle>
        {settings.printer?.serialNumber && (
          <IonList lines="none">
            <SectionListHeader>{t`Connected Printer`}</SectionListHeader>
            <IonItem>
              <IonAvatar slot="start">
                <Icon icon={printOutline} />
              </IonAvatar>
              <IonLabel color={selectedPrinter ? undefined : 'danger'}>
                <SelectedPrinter>{settings.printer.serialNumber}</SelectedPrinter>
                {!selectedPrinter && <div>{t`No Connection`}</div>}
              </IonLabel>
            </IonItem>
          </IonList>
        )}
        <IonList lines="none">
          <SectionListHeader>
            {t`Available Printers`}
            {isFetchingPrinters && (
              <>
                &nbsp;
                <IonSpinner />
              </>
            )}
          </SectionListHeader>
          {selectablePrinters?.map(printer => (
            <IonItem
              key={printer.serialNumber}
              onClick={() => saveSettings({ ...settings, printer })}
            >
              <IonAvatar slot="start">
                <Icon icon={bluetoothOutline} />
              </IonAvatar>
              <IonLabel>{printer.serialNumber}</IonLabel>
            </IonItem>
          ))}
        </IonList>
      </IonContent>
      <Footer>
        <SearchAgainText>{t`Don’t see the printer you are looking for?`}</SearchAgainText>
        <Button expand="block" fill="outline" onClick={rescan}>
          {t`Scan for Printers`}
        </Button>
        <Button
          expand="block"
          fill="outline"
          onClick={() => BadgePrinter.openNativeBluetoothSettings()}
        >
          {t`Open Device Bluetooth Settings`}
        </Button>
      </Footer>
    </TabLayout>
  );
};

export default Selection;
